import { Quaternion, Vector3, type Object3D } from 'three';

/**
 * Pose de reposo del cuerpo (tanda 4, D-1).
 *
 * Los avatares se entregan en **T-pose**: brazos rectos hacia los lados, a
 * la altura del hombro. En el encuadre de busto eso se ve como una franja
 * horizontal cruzando el borde inferior del cuadro — verificado en la
 * sesión real el 2026-09-19. Una persona en videollamada tiene los brazos
 * abajo, junto al cuerpo.
 *
 * Esta capa corre **una vez al cargar el asset** y no se anima: solo deja
 * el esqueleto en una postura creíble para que el resto de las capas
 * (idle, expresión, habla) trabajen encima.
 *
 * Los huesos se buscan por una lista de nombres candidatos, porque la
 * convención depende de la herramienta con la que se produjo el avatar. El
 * de desarrollo usa nombres de Mixamo (`LeftArm`, `LeftForeArm`); los de
 * producción vienen de Character Creator (`CC_Base_L_Upperarm`).
 */

/** Grados a radianes, para escribir las constantes en grados. */
const grados = (g: number): number => (g * Math.PI) / 180;

/**
 * Separación de los brazos respecto de la vertical. 0° sería pegarlos al
 * torso, que atraviesa la malla; 12° deja el hueco natural de la axila.
 */
const BRAZO_SEPARACION_RAD = grados(12);
/**
 * Cuánto se adelantan los brazos respecto del plano del torso. Sin esto
 * quedan como un soldado en posición de firmes.
 */
const BRAZO_ADELANTO_RAD = grados(8);
/** Flexión del codo. Un brazo perfectamente recto se lee como rigidez. */
const CODO_FLEXION_RAD = grados(14);
/**
 * Caída de la clavícula por debajo de la horizontal. En el encuadre de
 * busto los brazos no se ven: lo que se ve es la línea del hombro, y una
 * clavícula horizontal la deja recta, "hombros cuadrados" (2026-10-15,
 * avatares de Character Creator, que la traen a 90° de la vertical).
 *
 * El tope lo pone el pelo, no el hombro: el pelo largo está pesado a la
 * cabeza y no acompaña la caída. Medido en `Fem_Joven_1`: a 20° la línea
 * del hombro queda natural, pero las puntas flotan por encima; a 10° el
 * pelo sigue apoyado y el hombro ya no se lee recto.
 */
const CLAVICULA_CAIDA_RAD = grados(10);

/**
 * Nombres candidatos por hueso, en orden de preferencia. Cubren Mixamo
 * (`LeftArm`, con o sin prefijo `mixamorig:`), Ready Player Me (usa
 * Mixamo), VRM (`J_Bip_L_UpperArm`), Unreal (`upperarm_l`), Character
 * Creator (`CC_Base_L_Upperarm`) y variantes genéricas. La comparación
 * ignora mayúsculas, espacios, `_`, `.` y `:`.
 *
 * Sin los de Character Creator, los ocho avatares de producción quedaban
 * con la pose del asset y sin respiración (2026-10-15): los hombros se
 * veían cuadrados y el aviso de huesos faltantes salía en consola.
 */
export const HUESOS_CUERPO = {
  brazoIzq: [
    'LeftArm',
    'mixamorigLeftArm',
    'LeftUpperArm',
    'JBipLUpperArm',
    'CCBaseLUpperarm',
    'upperarml',
    'armL',
  ],
  brazoDer: [
    'RightArm',
    'mixamorigRightArm',
    'RightUpperArm',
    'JBipRUpperArm',
    'CCBaseRUpperarm',
    'upperarmr',
    'armR',
  ],
  antebrazoIzq: [
    'LeftForeArm',
    'mixamorigLeftForeArm',
    'LeftLowerArm',
    'JBipLLowerArm',
    'CCBaseLForearm',
    'lowerarml',
    'forearmL',
  ],
  antebrazoDer: [
    'RightForeArm',
    'mixamorigRightForeArm',
    'RightLowerArm',
    'JBipRLowerArm',
    'CCBaseRForearm',
    'lowerarmr',
    'forearmR',
  ],
  claviculaIzq: [
    'LeftShoulder',
    'mixamorigLeftShoulder',
    'JBipLShoulder',
    'CCBaseLClavicle',
    'claviclel',
    'shoulderL',
  ],
  claviculaDer: [
    'RightShoulder',
    'mixamorigRightShoulder',
    'JBipRShoulder',
    'CCBaseRClavicle',
    'clavicler',
    'shoulderR',
  ],
  /** Para la respiración (D-3). Se prefiere el hueso más alto del tronco. */
  pecho: [
    'Spine2',
    'mixamorigSpine2',
    'UpperChest',
    'Chest',
    'JBipC_UpperChest',
    'CCBaseSpine02',
    'spine03',
  ],
} as const;

/** Normaliza un nombre de hueso para comparar entre convenciones. */
function normalizar(nombre: string): string {
  return nombre.toLowerCase().replace(/[\s_.:-]/g, '');
}

/**
 * Primer hueso de la escena que coincide con alguno de los candidatos, o
 * `null`. A diferencia de `requerirHueso` (`mallas.ts`), acá la ausencia
 * no es un error: un asset sin brazos se sigue mostrando.
 */
export function buscarHueso(raiz: Object3D, candidatos: readonly string[]): Object3D | null {
  const buscados = candidatos.map(normalizar);
  let encontrado: Object3D | null = null;
  let mejor = Number.MAX_SAFE_INTEGER;
  raiz.traverse((obj) => {
    const i = buscados.indexOf(normalizar(obj.name));
    if (i >= 0 && i < mejor) {
      mejor = i;
      encontrado = obj;
    }
  });
  return encontrado;
}

/** Huesos que la pose de reposo y la respiración necesitan. */
export interface HuesosCuerpo {
  brazoIzq: Object3D | null;
  brazoDer: Object3D | null;
  antebrazoIzq: Object3D | null;
  antebrazoDer: Object3D | null;
  claviculaIzq: Object3D | null;
  claviculaDer: Object3D | null;
  pecho: Object3D | null;
}

export function buscarHuesosCuerpo(raiz: Object3D): HuesosCuerpo {
  return {
    brazoIzq: buscarHueso(raiz, HUESOS_CUERPO.brazoIzq),
    brazoDer: buscarHueso(raiz, HUESOS_CUERPO.brazoDer),
    antebrazoIzq: buscarHueso(raiz, HUESOS_CUERPO.antebrazoIzq),
    antebrazoDer: buscarHueso(raiz, HUESOS_CUERPO.antebrazoDer),
    claviculaIzq: buscarHueso(raiz, HUESOS_CUERPO.claviculaIzq),
    claviculaDer: buscarHueso(raiz, HUESOS_CUERPO.claviculaDer),
    pecho: buscarHueso(raiz, HUESOS_CUERPO.pecho),
  };
}

/**
 * Dirección del hueso en el mundo: hacia dónde apunta el miembro. Se toma
 * del hijo **más lejano**, que en un esqueleto es el extremo del segmento
 * (del brazo al codo, del codo a la mano). No sirve el primer hijo: en
 * Character Creator el antebrazo tiene antes que la mano huesos de giro
 * (`ForearmTwist01`, `ElbowShareBone`) en su mismo origen, y con ellos el
 * codo quedaba recto. Devuelve `null` si no hay hijo o si todos están en
 * la misma posición.
 */
function direccionMundo(hueso: Object3D): Vector3 | null {
  const desde = hueso.getWorldPosition(new Vector3());
  const hasta = new Vector3();
  const dir = new Vector3();
  for (const hijo of hueso.children) {
    hijo.getWorldPosition(hasta).sub(desde);
    if (hasta.lengthSq() > dir.lengthSq()) dir.copy(hasta);
  }
  return dir.lengthSq() < 1e-8 ? null : dir.normalize();
}

/**
 * Apunta un hueso hacia `destino` (dirección en mundo), respetando la
 * orientación local del esqueleto: no asume qué eje local recorre el
 * miembro, lo deduce de dónde está su hijo.
 *
 * **Es idempotente**: aplicado dos veces sobre la misma escena, la segunda
 * no mueve nada, porque el hueso ya apunta al destino. Importa porque
 * `useGLTF` cachea la escena y StrictMode monta dos veces.
 */
function apuntar(hueso: Object3D, destino: Vector3): boolean {
  const actual = direccionMundo(hueso);
  if (!actual) return false;

  const delta = new Quaternion().setFromUnitVectors(actual, destino.clone().normalize());
  const enMundo = hueso.getWorldQuaternion(new Quaternion());
  const objetivo = delta.multiply(enMundo);
  const padre = hueso.parent?.getWorldQuaternion(new Quaternion()) ?? new Quaternion();
  hueso.quaternion.copy(padre.invert().multiply(objetivo));
  hueso.updateMatrixWorld(true);
  return true;
}

/**
 * Inclina la clavícula `CLAVICULA_CAIDA_RAD` por debajo de la horizontal,
 * conservando su dirección en planta (hacia afuera y algo hacia atrás).
 * Idempotente por construcción: el destino depende sólo de la proyección
 * horizontal, que la inclinación no cambia. Sin clavícula no pasa nada:
 * es una mejora, no un requisito, y no se informa como faltante.
 */
function bajarClavicula(clavicula: Object3D | null): void {
  if (!clavicula) return;
  const actual = direccionMundo(clavicula);
  if (!actual) return;
  const planta = new Vector3(actual.x, 0, actual.z);
  if (planta.lengthSq() < 1e-8) return;
  planta.normalize().multiplyScalar(Math.cos(CLAVICULA_CAIDA_RAD));
  apuntar(clavicula, planta.setY(-Math.sin(CLAVICULA_CAIDA_RAD)));
}

/**
 * Baja hombros y brazos a una pose relajada. Devuelve qué huesos de brazo
 * no encontró, para que el llamador avise: **la sesión funciona igual**,
 * con los brazos donde vinieron.
 *
 * El destino se expresa como dirección en el mundo (abajo, un poco hacia
 * afuera y un poco hacia adelante), así que sirve a cualquier esqueleto que
 * mire a +Z con +Y arriba, que es lo que exige `06_ESTANDAR_RIG.md` §1.
 */
export function aplicarPoseDeReposo(raiz: Object3D, huesos: HuesosCuerpo): string[] {
  raiz.updateMatrixWorld(true);
  const faltan: string[] = [];

  // `lado` es +1 para el brazo izquierdo del avatar, que en coordenadas de
  // mundo cae en +X (mira a +Z con +Y arriba: su izquierda es +X).
  const bajar = (
    brazo: Object3D | null,
    antebrazo: Object3D | null,
    lado: 1 | -1,
    nombre: string,
  ) => {
    if (!brazo) {
      faltan.push(nombre);
      return;
    }
    const abajo = new Vector3(
      lado * Math.sin(BRAZO_SEPARACION_RAD),
      -Math.cos(BRAZO_SEPARACION_RAD),
      Math.sin(BRAZO_ADELANTO_RAD),
    );
    if (!apuntar(brazo, abajo)) faltan.push(nombre);
    if (!antebrazo) return;
    // El antebrazo sigue al brazo con algo de flexión hacia adelante.
    const flexionado = new Vector3(
      lado * Math.sin(BRAZO_SEPARACION_RAD),
      -Math.cos(BRAZO_SEPARACION_RAD + CODO_FLEXION_RAD),
      Math.sin(BRAZO_ADELANTO_RAD + CODO_FLEXION_RAD),
    );
    apuntar(antebrazo, flexionado);
  };

  // La clavícula primero: los brazos se apuntan en coordenadas de mundo,
  // así que no heredan su giro.
  bajarClavicula(huesos.claviculaIzq);
  bajarClavicula(huesos.claviculaDer);
  bajar(huesos.brazoIzq, huesos.antebrazoIzq, 1, 'brazo izquierdo');
  bajar(huesos.brazoDer, huesos.antebrazoDer, -1, 'brazo derecho');
  return faltan;
}
